import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Pagination,
  Paper,
} from '@mui/material';
import { Add as AddIcon, StickyNote2 as NoteIcon } from '@mui/icons-material';
import { NoteCard, NoteFilters, NoteDetailModal } from '../../components/notes';
import { useNotes, useNoteSearch } from '../../hooks/useNotes';
import { useNoteMutations } from '../../hooks/useNoteMutations';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type {
  NoteFilters as NoteFiltersType,
  NoteWithRelations,
  CreateNoteRequest,
  UpdateNoteRequest,
} from '../../types/note';

export const NotesPage: React.FC = () => {
  const [filters, setFilters] = useState<NoteFiltersType>({
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    archived: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<NoteWithRelations | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<NoteWithRelations | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data, isLoading } = useNotes(filters);
  const { data: searchData, isLoading: searchLoading } = useNoteSearch(debouncedSearch, {
    enabled: debouncedSearch.length >= 2,
  });
  const mutations = useNoteMutations();

  const isSearching = debouncedSearch.length >= 2;
  const notes = isSearching ? searchData?.data?.notes || [] : data?.data?.notes || [];
  const pagination = isSearching ? null : data?.data?.pagination;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSaveNote = async (formData: CreateNoteRequest | UpdateNoteRequest) => {
    try {
      if (selectedNote) {
        await mutations.updateNote.mutateAsync({
          id: selectedNote._id,
          data: formData as UpdateNoteRequest,
        });
        setSnackbar({ open: true, message: 'Note updated', severity: 'success' });
      } else {
        await mutations.createNote.mutateAsync(formData as CreateNoteRequest);
        setSnackbar({ open: true, message: 'Note created', severity: 'success' });
      }
      setIsDetailModalOpen(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Failed to save',
        severity: 'error',
      });
      throw error;
    }
  };

  const handlePinNote = async (note: NoteWithRelations) => {
    await mutations.pinNote.mutateAsync(note._id);
    setSnackbar({
      open: true,
      message: note.isPinned ? 'Unpinned' : 'Pinned',
      severity: 'success',
    });
  };

  const handleArchiveNote = async (note: NoteWithRelations) => {
    await mutations.archiveNote.mutateAsync(note._id);
    setSnackbar({
      open: true,
      message: note.isArchived ? 'Restored' : 'Archived',
      severity: 'success',
    });
    setIsDetailModalOpen(false);
  };

  const handleConvertToTask = async (note: NoteWithRelations) => {
    await mutations.convertToTask.mutateAsync(note._id);
    setSnackbar({ open: true, message: 'Converted to task', severity: 'success' });
    setIsDetailModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;
    await mutations.deleteNote.mutateAsync(noteToDelete._id);
    setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
    setNoteToDelete(null);
  };

  const handleOpenNewNote = () => {
    setSelectedNote(null);
    setIsDetailModalOpen(true);
  };

  const handleEditNote = (note: NoteWithRelations) => {
    setSelectedNote(note);
    setIsDetailModalOpen(true);
  };

  const handleDeleteNote = (note: NoteWithRelations) => {
    setNoteToDelete(note);
    setIsDetailModalOpen(false);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pagination?.total || notes.length} notes
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNewNote}>
          New Note
        </Button>
      </Box>

      {/* Filters */}
      <NoteFilters
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={setSearchQuery}
        searchQuery={searchQuery}
      />

      {/* Content */}
      {isLoading || searchLoading ? (
        <Typography>Loading...</Typography>
      ) : notes.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 8 }}>
          <NoteIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6">No notes yet</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Create your first note to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenNewNote}
          >
            Create Your First Note
          </Button>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2}>
            {notes.map((note) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={note._id}>
                <NoteCard
                  note={note}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                  onPin={handlePinNote}
                  onArchive={handleArchiveNote}
                  onConvertToTask={handleConvertToTask}
                />
              </Grid>
            ))}
          </Grid>
          {pagination && pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(_, p) => setFilters((f) => ({ ...f, page: p }))}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Modal */}
      <NoteDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        note={selectedNote}
        onSave={handleSaveNote}
        onPin={handlePinNote}
        onArchive={handleArchiveNote}
        onConvertToTask={handleConvertToTask}
        onDelete={handleDeleteNote}
        isLoading={mutations.createNote.isPending || mutations.updateNote.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!noteToDelete}
        title="Delete Note"
        message={`Delete "${noteToDelete?.title}"?`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setNoteToDelete(null)}
        loading={mutations.deleteNote.isPending}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotesPage;
